import React, { useState, useEffect, useRef } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { taskAPI, listAPI, timerAPI, timelineAPI } from '../lib/api';
import api from '../lib/api';
import { Layout } from '../components/Layout';
import { TaskModal } from '../components/TaskModal';
import { TaskDetailFullModal } from '../components/TaskDetailFullModal';
import SpaceChannelSlideOver from '../components/SpaceChannelSlideOver';
import CreateSpaceChannelModal from '../components/CreateSpaceChannelModal';
import SearchableSelect from '../components/SearchableSelect';
import SCurveChart from '../components/SCurveChart';
import SCurveAllTimeChart from '../components/SCurveAllTimeChart';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Link } from 'react-router-dom';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { 
    cn, 
    formatDate, 
    priorityConfig, 
    isOverdue,
    getInitials,
    formatDuration
} from '../lib/utils';
import {
    Plus,
    LayoutGrid,
    List,
    GanttChart,
    Filter,
    ChevronDown,
    Clock,
    Play,
    Calendar,
    CheckCircle2,
    Settings2,
    Trash2,
    Edit2,
    ListTodo,
    Loader2,
    UserPlus,
    PanelRightClose,
    Square,
    MessageSquare,
    Hash,
    TrendingUp,
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addDays, differenceInDays, parseISO, subDays } from 'date-fns';
import FieldStatus from '@/components/Field/FieldStatus';
import { Checkbox } from '@/components/ui/checkbox';
import { useTimerContext } from '@/context/TimerContext';
export default function TasksPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { 
        currentWorkspace, 
        currentSpace, 
        spaces, 
        lists, 
        tasks, 
        members,
        selectSpace,
        refreshTasks,
        addTask,
        setLists,
        fetchWorkspaces,
        selectWorkspace,
        workspaces,
        isReadOnly,
        canEditTasks,
        currentUserRole
    } = useWorkspace();
    
    const [viewMode, setViewMode] = useState('list');
    const [selectedList, setSelectedList] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [filterPriority, setFilterPriority] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterAssignee, setFilterAssignee] = useState('all');
    const [filterDueDate, setFilterDueDate] = useState('');
    const [filterDueDateNull, setFilterDueDateNull] = useState(false);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterStartDateNull, setFilterStartDateNull] = useState(false);
    const [initializing, setInitializing] = useState(true);
    
    // Active timer state - for highlighting task with running timer
    const {activeTimer, setActiveTimer} = useTimerContext();
    
    // Full modal state for detail view
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    
    // Split-screen panel state (legacy - keeping for board view)
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [isCreatingTask, setIsCreatingTask] = useState(false);
    
    // List management modal
    const [isListModalOpen, setIsListModalOpen] = useState(false);
    const [editingList, setEditingList] = useState(null);
    
    // Space channel state
    const [spaceChannel, setSpaceChannel] = useState(null);
    const [isChannelSlideOpen, setIsChannelSlideOpen] = useState(false);
    const [loadingChannel, setLoadingChannel] = useState(false);
    const [showCreateChannelModal, setShowCreateChannelModal] = useState(false);
    
    // S-Curve modal state
    const [showSCurveModal, setShowSCurveModal] = useState(false);

    // Auto-select first workspace if none selected
    useEffect(() => {
        const initWorkspace = async () => {
            if (!currentWorkspace) {
                const ws = await fetchWorkspaces();
                if (ws.length > 0) {
                    await selectWorkspace(ws[0]);
                }
            }
            setInitializing(false);
        };
        initWorkspace();
    }, []);

    useEffect(() => {
        // Check for space param in URL
        const spaceId = searchParams.get('space');
        if (spaceId && spaces.length > 0) {
            const space = spaces.find(s => s.id === spaceId);
            if (space) selectSpace(space);
        } else if (!currentSpace && spaces.length > 0) {
            // Auto-select first space if none selected
            selectSpace(spaces[0]);
        }
    }, [searchParams, spaces, currentSpace, selectSpace]);

    // Sync selectedList when ?list= param changes (e.g. clicking a list in sidebar)
    useEffect(() => {
        const listId = searchParams.get('list');
        if (listId && lists.length > 0) {
            const list = lists.find(l => l.id === listId);
            if (list && list.id !== selectedList?.id) {
                setSelectedList(list);
            }
        }
    }, [searchParams, lists]);

    // Deep-link support: open task detail from ?task=<id>. Runs once per taskId.
    const openedTaskIdRef = useRef(null);
    const pendingSpaceIdRef = useRef(null);
    const pendingListIdRef = useRef(null);
    useEffect(() => {
        const taskId = searchParams.get('task');
        if (!taskId) {
            openedTaskIdRef.current = null;
            return;
        }
        if (openedTaskIdRef.current === taskId) return;
        if (!workspaces || workspaces.length === 0) return; // wait for workspaces to load
        openedTaskIdRef.current = taskId;
        loadTaskAndOpen(taskId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, workspaces]);

    useEffect(() => {
        if (currentSpace && lists.length > 0) {
            // Respect ?list= URL param if present, otherwise default to first list
            const listId = searchParams.get('list');
            if (listId) {
                const match = lists.find(l => l.id === listId);
                setSelectedList(match || lists[0]);
            } else if (pendingListIdRef.current) {
                const match = lists.find(l => l.id === pendingListIdRef.current);
                setSelectedList(match || lists[0]);
                pendingListIdRef.current = null;
            } else {
                setSelectedList(lists[0]);
            }
        }
    }, [currentSpace, lists]);

    // Apply pending space selection from deep-link after spaces load for the target workspace
    useEffect(() => {
        if (pendingSpaceIdRef.current && spaces.length > 0) {
            const target = spaces.find(s => s.id === pendingSpaceIdRef.current);
            if (target) {
                selectSpace(target);
                pendingSpaceIdRef.current = null;
            }
        }
    }, [spaces, selectSpace]);

    // Load space channel when space changes
    useEffect(() => {
        if (currentSpace && currentWorkspace) {
            loadSpaceChannel();
        } else {
            setSpaceChannel(null);
        }
    }, [currentSpace, currentWorkspace]);

    const loadSpaceChannel = async () => {
        if (!currentSpace || !currentWorkspace) return;
        
        setLoadingChannel(true);
        try {
            const { data } = await api.get(
                `/workspaces/${currentWorkspace.id}/channels/spaces/${currentSpace.id}/channel`
            );
            console.log('Loaded space channel:', data);
            setSpaceChannel(data);
        } catch (error) {
            console.log('No channel yet or access denied:', error.response?.status);
            // No channel yet or access denied, that's fine
            setSpaceChannel(null);
        } finally {
            setLoadingChannel(false);
        }
    };

    const handleOpenChannel = () => {
        console.log('handleOpenChannel called', { 
            hasChannel: !!spaceChannel, 
            channelId: spaceChannel?.id,
            currentUserRole 
        });
        
        if (spaceChannel) {
            console.log('Opening channel SlideOver');
            setIsChannelSlideOpen(true);
        } else {
            console.log('No channel, checking role...');
            // Check if user is admin/owner
            if (currentUserRole === 'owner' || currentUserRole === 'admin') {
                console.log('Admin/Owner - opening create modal');
                setShowCreateChannelModal(true);
            } else {
                console.log('Not admin - showing info toast');
                toast.info('No channel for this space yet. Ask admin to create one.');
            }
        }
    };

    const handleChannelCreated = (channelData) => {
        console.log('Channel created successfully:', channelData);
        
        // Immediately set the channel data
        setSpaceChannel(channelData);
        setShowCreateChannelModal(false);
        
        toast.success(`Channel #${channelData.name} created!`);
        
        // Open SlideOver after a short delay
        setTimeout(() => {
            console.log('Opening SlideOver with channel:', channelData.id);
            setIsChannelSlideOpen(true);
        }, 300);
    };

    const handleCloseSlideOver = () => {
        setIsChannelSlideOpen(false);
        // Reload channel data when closing to get fresh data
        if (currentSpace && currentWorkspace) {
            loadSpaceChannel();
        }
    };


    // Check for active timer on mount and periodically
    // useEffect(() => {
    //     checkActiveTimer();
    // }, []);

    const loadTaskAndOpen = async (taskId) => {
        try {
            const response = await taskAPI.get(taskId);
            const task = response.data;
            
            // Switch workspace if task belongs to a different one
            if (task?.workspace_id && currentWorkspace?.id !== task.workspace_id) {
                const targetWs = (workspaces || []).find(w => w.id === task.workspace_id);
                if (targetWs) {
                    await selectWorkspace(targetWs);
                } else {
                    toast.error('Task ada di workspace lain yang tidak Anda ikuti');
                    setSearchParams({});
                    return;
                }
            }
            
            // Switch space if task belongs to a different one
            if (task?.space_id) {
                // spaces list will re-load after workspace switch; defer selection via ref
                pendingSpaceIdRef.current = task.space_id;
                pendingListIdRef.current = task.list_id || null;
            }
            
            setSelectedTask(task);
            setIsDetailModalOpen(true);
        } catch (error) {
            const status = error?.response?.status;
            if (status === 403) {
                toast.error('Tidak punya akses ke task ini');
            } else if (status === 404) {
                toast.error('Task tidak ditemukan');
            } else {
                toast.error('Gagal memuat task');
            }
            setSearchParams({});
        }
    };

    const handleCreateTask = () => {
        setSelectedTask(null);
        setIsCreatingTask(true);
        setIsModalOpen(true);
    };

    const handleTaskClick = (task) => {
        console.log('selected task:', task);
        console.log('selected list:', selectedList);
        setSelectedTask(task);
        setIsCreatingTask(false);
        setIsDetailModalOpen(true);
        setSearchParams({ task: task.id });
    };

    const handleDetailModalClose = () => {
        setIsDetailModalOpen(false);
        setSelectedTask(null);
        setSearchParams({});
    };

    const handlePanelClose = () => {
        setIsPanelOpen(false);
        setSelectedTask(null);
        setIsCreatingTask(false);
        setSearchParams({});
        refreshTasks();
    };

    // Keep modal for backward compatibility
    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedTask(null);
        setSearchParams({});
        refreshTasks();
    };

    const handleStartTimer = async (e, task) => {
        e.stopPropagation();
        try {
            const response = await timerAPI.start(task.id);
            console.log('Timer started:', response.data);
            setActiveTimer(response.data);
            toast.success(`Timer started for "${task.title}"`);
            refreshTasks();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to start timer');
        }
    };

    const handleStopTimer = async (e, task) => {
        e.stopPropagation();
        try {
            await timerAPI.stop();
            setActiveTimer(null);
            toast.success('Timer stopped');
            refreshTasks();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to stop timer');
        }
    };

    const handleQuickStatusChange = async (taskId) => {
        try {
            await taskAPI.quickChangeStatus(taskId);
            refreshTasks();
            toast.success('Status updated');
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleUpdateStatus = async (taskId, status) => {
        try {
            await taskAPI.update(taskId, { status });
            refreshTasks();
            toast.success('Task updated');
            setIsListModalOpen(false);
            setEditingList(null);
        } catch (error) {
            toast.error('Failed to update list');
        }
    };

        // List management handlers
    const handleCreateList = async (data) => {
        try {
            const response = await listAPI.create({ ...data, space_id: currentSpace.id });
            setLists([...lists, response.data]);
            toast.success('List created');
            setIsListModalOpen(false);
            setEditingList(null);
        } catch (error) {
            toast.error('Failed to create list');
        }
    };

    const handleUpdateList = async (listId, data) => {
        try {
            await listAPI.update(listId, data);
            const updated = lists.map(l => l.id === listId ? { ...l, ...data } : l);
            setLists(updated);
            toast.success('List updated');
            setIsListModalOpen(false);
            setEditingList(null);
        } catch (error) {
            toast.error('Failed to update list');
        }
    };

    const handleDeleteList = async (listId) => {
        if (!window.confirm('Delete this list and all its tasks?')) return;
        try {
            await listAPI.delete(listId);
            setLists(lists.filter(l => l.id !== listId));
            if (selectedList?.id === listId) {
                setSelectedList(lists[0] || null);
            }
            toast.success('List deleted');
        } catch (error) {
            toast.error('Failed to delete list');
        }
    };

    const filteredTasks = tasks.filter(task => {
        if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
        if (filterStatus !== 'all' && task.status !== filterStatus) return false;
        if (filterAssignee !== 'all') {
            // Check if task has this assignee
            const assigneeIds = task.assignee_ids || [];
            if (!assigneeIds.includes(filterAssignee)) return false;
        }
        if (filterDueDateNull && task.due_date) return false;
        if (filterDueDate && task.due_date?.slice(0, 10) !== filterDueDate) return false;
        if (filterStartDateNull && task.start_date) return false;
        if (filterStartDate && task.start_date?.slice(0, 10) !== filterStartDate) return false;
        if (selectedList && task.list_id !== selectedList.id) return false;
        return true;
    });

    const tasksByStatus = {};
    const statuses = selectedList?.statuses || [
        { name: 'To Do', color: '#9FB0AC' },
        { name: 'In Progress', color: '#6EC1E4' },
        { name: 'Review', color: '#F5C26B' },
        { name: 'Done', color: '#7ED957' },
    ];
    
    // Group tasks by status (case-insensitive matching)
    statuses.forEach(status => {
        const statusLower = status.name.toLowerCase();
        tasksByStatus[status.name] = filteredTasks.filter(t => 
            t.status?.toLowerCase() === statusLower
        );
    });

    if (initializing || !currentWorkspace) {
        return (
            <Layout title="Tasks">
                <div className="flex items-center justify-center h-64">
                    <div className="spinner" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Tasks" subtitle={currentSpace?.name || 'All tasks'}>
            <div className="flex h-[calc(100vh-120px)]" data-testid="tasks-page">
                {/* Main Content Area */}
                <div className={cn(
                    "flex-1 transition-all duration-300 overflow-auto",
                    isPanelOpen ? "mr-0" : ""
                )}>
                    <div className="space-y-4 p-0">
                        {/* Toolbar */}
                        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                    <div className="flex items-center gap-3 flex-wrap">
                        {/* Space Selector */}
                        <SearchableSelect
                            value={currentSpace?.id || ''}
                            onChange={(val) => {
                                const space = spaces.find(s => s.id === val);
                                selectSpace(space);
                            }}
                            options={spaces.map(space => ({
                                value: space.id,
                                label: space.name,
                                color: space.color,
                            }))}
                            placeholder="Select Space"
                            className="w-48"
                        />

                        {/* List Selector with Add Button */}
                        <div className="flex items-center gap-1">
                            {lists.length > 0 ? (
                                <Select 
                                    value={selectedList?.id || ''} 
                                    onValueChange={(val) => {
                                        const list = lists.find(l => l.id === val);
                                        setSelectedList(list);
                                    }}
                                >
                                    <SelectTrigger className="w-40" data-testid="list-selector">
                                        <SelectValue placeholder="Select List" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {lists.map(list => (
                                            <SelectItem key={list.id} value={list.id}>
                                                {list.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <span className="text-sm text-brand-text-muted px-2">No lists <Link className="text-brand-primary border-b border-dashed border-brand-primary mx-2" to={`/admin/settings/?tab=lists&wsid=${currentWorkspace.id}`}>Go to Settings</Link> </span>
                            )}
                            
                            {/* Add/Manage List Button - only for non-client users */}
                            {/* {canEditTasks && (
                                <Button 
                                    variant="outline" 
                                    size="icon"
                                    className="h-9 w-9"
                                    onClick={() => { setEditingList(null); setIsListModalOpen(true); }}
                                    data-testid="manage-lists-btn"
                                >
                                    <Plus className="w-4 h-4" strokeWidth={1.5} />
                                </Button>
                            )} */}
                        </div>

                        {/* Filters */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" data-testid="filter-btn">
                                    <Filter className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                    Filter
                                    {[filterPriority !== 'all', filterAssignee !== 'all', filterDueDate !== '', filterDueDateNull, filterStartDate !== '', filterStartDateNull].some(Boolean) && (
                                        <span className="ml-1 px-1.5 py-0.5 text-xs bg-brand-primary text-white rounded-full">
                                            {[filterPriority !== 'all', filterAssignee !== 'all', filterDueDate !== '', filterDueDateNull, filterStartDate !== '', filterStartDateNull].filter(Boolean).length}
                                        </span>
                                    )}
                                    <ChevronDown className="w-4 h-4 ml-2" strokeWidth={1.5} />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-56">
                                <div className="p-2 space-y-3">
                                    <div>
                                        <p className="text-xs font-semibold text-brand-text-muted mb-2">Priority</p>
                                        <Select value={filterPriority} onValueChange={setFilterPriority}>
                                            <SelectTrigger className="h-8">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All Priorities</SelectItem>
                                                <SelectItem value="urgent">Urgent</SelectItem>
                                                <SelectItem value="high">High</SelectItem>
                                                <SelectItem value="medium">Medium</SelectItem>
                                                <SelectItem value="low">Low</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-brand-text-muted mb-2">Assignee</p>
                                        <Select value={filterAssignee} onValueChange={setFilterAssignee}>
                                            <SelectTrigger className="h-8">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All Members</SelectItem>
                                                {members.map(member => (
                                                    <SelectItem key={member.user_id} value={member.user_id}>
                                                        {member.user_name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-brand-text-muted mb-2">Due Date</p>
                                        <input
                                            type="date"
                                            value={filterDueDate}
                                            disabled={filterDueDateNull}
                                            onChange={e => setFilterDueDate(e.target.value)}
                                            className="w-full h-8 px-2 text-sm border rounded-md bg-background text-brand-text-primary disabled:opacity-40"
                                        />
                                        <label className="flex items-center gap-2 mt-2 cursor-pointer">
                                            <Checkbox
                                                checked={filterDueDateNull}
                                                onCheckedChange={checked => {
                                                    setFilterDueDateNull(checked);
                                                    if (checked) setFilterDueDate('');
                                                }}
                                            />
                                            <span className="text-xs text-brand-text-muted">No due date</span>
                                        </label>
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-brand-text-muted mb-2">Start Date</p>
                                        <input
                                            type="date"
                                            value={filterStartDate}
                                            disabled={filterStartDateNull}
                                            onChange={e => setFilterStartDate(e.target.value)}
                                            className="w-full h-8 px-2 text-sm border rounded-md bg-background text-brand-text-primary disabled:opacity-40"
                                        />
                                        <label className="flex items-center gap-2 mt-2 cursor-pointer">
                                            <Checkbox
                                                checked={filterStartDateNull}
                                                onCheckedChange={checked => {
                                                    setFilterStartDateNull(checked);
                                                    if (checked) setFilterStartDate('');
                                                }}
                                            />
                                            <span className="text-xs text-brand-text-muted">No start date</span>
                                        </label>
                                    </div>
                                    {[filterPriority !== 'all', filterAssignee !== 'all', filterDueDate !== '', filterDueDateNull, filterStartDate !== '', filterStartDateNull].some(Boolean) && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-full text-brand-text-muted"
                                            onClick={() => {
                                                setFilterPriority('all');
                                                setFilterAssignee('all');
                                                setFilterDueDate('');
                                                setFilterDueDateNull(false);
                                                setFilterStartDate('');
                                                setFilterStartDateNull(false);
                                            }}
                                        >
                                            Clear Filters
                                        </Button>
                                    )}
                                </div>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Space Channel Button */}
                        {currentSpace && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleOpenChannel}
                                disabled={loadingChannel}
                                className={cn(
                                    "gap-2",
                                    spaceChannel && "text-brand-primary border-brand-primary hover:bg-brand-primary/10"
                                )}
                                title={spaceChannel ? `Open ${spaceChannel.name}` : "No channel yet"}
                            >
                                {loadingChannel ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : spaceChannel ? (
                                    <>
                                        <Hash className="w-4 h-4" />
                                        <span className="hidden sm:inline">Channel</span>
                                    </>
                                ) : (
                                    <>
                                        <MessageSquare className="w-4 h-4" />
                                        <span className="hidden sm:inline">Channel</span>
                                    </>
                                )}
                            </Button>
                        )}
                        
                        {/* S-Curve Button */}
                        {currentSpace && currentWorkspace && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowSCurveModal(true)}
                                className="gap-2"
                                title="Lihat Kurva S untuk space ini"
                            >
                                <TrendingUp className="w-4 h-4" />
                                <span className="hidden sm:inline">Kurva S</span>
                            </Button>
                        )}
                        
                        {/* View Toggle - Board / List / Timeline */}
                        <div className="flex items-center bg-brand-accent-soft rounded-lg p-1" data-testid="view-toggle">
                            <button
                                onClick={() => setViewMode('board')}
                                className={cn(
                                    'p-1.5 rounded-md transition-colors',
                                    viewMode === 'board' ? 'bg-white shadow-sm' : 'hover:bg-white/50'
                                )}
                                data-testid="view-board-btn"
                                title="Kanban Board"
                            >
                                <LayoutGrid className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={cn(
                                    'p-1.5 rounded-md transition-colors',
                                    viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-white/50'
                                )}
                                data-testid="view-list-btn"
                                title="List View"
                            >
                                <List className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                            <button
                                onClick={() => setViewMode('timeline')}
                                className={cn(
                                    'p-1.5 rounded-md transition-colors',
                                    viewMode === 'timeline' ? 'bg-white shadow-sm' : 'hover:bg-white/50'
                                )}
                                data-testid="view-timeline-btn"
                                title="Timeline View"
                            >
                                <GanttChart className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                        </div>

                        {/* Only show Add Task button if user can edit */}
                        {canEditTasks && (
                            <Button 
                                onClick={handleCreateTask}
                                className="bg-brand-primary hover:bg-brand-primary-dark"
                                disabled={!selectedList}
                                data-testid="create-task-btn"
                            >
                                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                Add Task
                            </Button>
                        )}
                    </div>
                </div>

                {/* Content */}
                {!currentSpace ? (
                    <div className="empty-state">
                        <p className="text-brand-text-muted">Select a space to view tasks</p>
                    </div>
                ) : viewMode === 'board' ? (
                    /* Board View */
                    <div className="kanban-container" data-testid="kanban-board">
                        {statuses.map((status) => (
                            <div key={status.name} className="kanban-column" data-testid={`kanban-column-${status.name}`}>
                                <div 
                                    className="kanban-column-header"
                                    style={{ borderColor: status.color }}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-sm text-brand-text-primary">
                                            {status.name}
                                        </span>
                                        <span className="text-xs text-brand-text-muted bg-brand-background px-2 py-0.5 rounded-full">
                                            {tasksByStatus[status.name]?.length || 0}
                                        </span>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={handleCreateTask}
                                        style={{ display: canEditTasks ? 'flex' : 'none' }}
                                    >
                                        <Plus className="w-4 h-4" strokeWidth={1.5} />
                                    </Button>
                                </div>
                                <ScrollArea className="flex-1">
                                    <div className="space-y-4 pt-3 pb-2">
                                        {tasksByStatus[status.name]?.map((task) => (
                                            <TaskCard 
                                                key={task.id}
                                                task={task}
                                                members={members}
                                                onClick={() => handleTaskClick(task)}
                                                onStartTimer={(e) => handleStartTimer(e, task)}
                                            />
                                        ))}
                                    </div>
                                </ScrollArea>
                            </div>
                        ))}
                    </div>
                ) : viewMode === 'timeline' ? (
                    /* Timeline View */
                    <TimelineView 
                        tasks={filteredTasks} 
                        statuses={statuses}
                        onTaskClick={handleTaskClick}
                        onStartTimer={handleStartTimer}
                        onStatusChange={handleQuickStatusChange}
                    />
                ) : (
                    /* List View - Grouped by Status */
                    <div className="space-y-6" data-testid="task-list">
                        {statuses.map((statusObj, index) => {
                            const statusTasks = filteredTasks.filter(t => t.status === statusObj.name);
                            if (statusTasks.length === 0) return null;
                            return (
                                <Card key={statusObj.name} className={cn("border-brand-border overflow-hidden", index > 0 && "mt-2")}>
                                    <div 
                                        className="px-4 py-2 border-b border-brand-border flex items-center gap-2"
                                        style={{ backgroundColor: `${statusObj.color}20` }}
                                    >
                                        <div 
                                            className="w-3 h-3 rounded-full" 
                                            style={{ backgroundColor: statusObj.color }} 
                                        />
                                        <span className="font-medium text-sm">{statusObj.name}</span>
                                        <span className="text-xs text-brand-text-muted">({statusTasks.length})</span>
                                    </div>
                                    <div className="divide-y divide-brand-border">
                                        {statusTasks.map((task) => (
                                            <TaskListItem 
                                                key={task.id}
                                                task={task}
                                                statuses={statuses}
                                                members={members}
                                                onClick={() => handleTaskClick(task)}
                                                onStatusChange={handleUpdateStatus}
                                                onQuickStatusChange={handleQuickStatusChange}
                                                onStartTimer={handleStartTimer}
                                                onStopTimer={handleStopTimer}
                                                isTimerActive={activeTimer?.task_id === task.id}
                                            />
                                        ))}
                                    </div>
                                </Card>
                            );
                        })}
                        {filteredTasks.length === 0 && (
                            <Card className="border-brand-border p-8 text-center text-brand-text-muted">
                                No tasks found. Create your first task!
                            </Card>
                        )}
                    </div>
                )}
                    </div>
                </div>

                {/* Split-Screen Task Detail Panel - Legacy for Board View */}
                {isPanelOpen && viewMode === 'board' && (
                    <div className="w-[450px] flex-shrink-0 border-l border-brand-border bg-white shadow-lg" data-testid="task-split-panel">
                        <TaskDetailFullModal
                            isOpen={isPanelOpen}
                            onClose={handlePanelClose}
                            task={selectedTask}
                            listId={selectedTask?.list_id || selectedList?.id}
                            statuses={statuses}
                            members={members}
                            readOnly={isReadOnly}
                            onTaskUpdate={() => {
                                refreshTasks();
                                // Refresh active timer
                                timerAPI.getActive().then(res => {
                                    setActiveTimer(res.data || null);
                                }).catch(() => setActiveTimer(null));
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Task Detail Full Modal */}
            {selectedTask && isDetailModalOpen && (
                <TaskDetailFullModal
                    isOpen={isDetailModalOpen}
                    onClose={handleDetailModalClose}
                    task={selectedTask}
                    listId={selectedTask?.list_id || selectedList?.id}
                    statuses={statuses}
                    members={members}
                    readOnly={isReadOnly}
                    onTaskUpdate={() => {
                        refreshTasks();
                        // Refresh active timer
                        timerAPI.getActive().then(res => {
                            setActiveTimer(res.data || null);
                        }).catch(() => setActiveTimer(null));
                    }}
                />
            )}

            {/* Task Modal - for creating new tasks */}
            <TaskModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                task={null}
                listId={selectedList?.id}
                workspaceId={currentWorkspace?.id}
                statuses={statuses}
                members={members}
                onTaskCreated={(newTask) => {
                    if (newTask) {
                        addTask(newTask);
                    } else {
                        refreshTasks();
                    }
                }}
            />

            {/* List Management Modal */}
            {/* <ListManagementModal
                isOpen={isListModalOpen}
                onClose={() => { setIsListModalOpen(false); setEditingList(null); }}
                lists={lists}
                editingList={editingList}
                onCreateList={handleCreateList}
                onUpdateList={handleUpdateList}
                onDeleteList={handleDeleteList}
                onEditList={setEditingList}
            /> */}
            
            {/* Space Channel SlideOver */}
            {spaceChannel && (
                <SpaceChannelSlideOver
                    isOpen={isChannelSlideOpen}
                    onClose={handleCloseSlideOver}
                    channel={spaceChannel}
                    space={currentSpace}
                />
            )}
            
            {/* Create Space Channel Modal */}
            <CreateSpaceChannelModal
                isOpen={showCreateChannelModal}
                onClose={() => setShowCreateChannelModal(false)}
                space={currentSpace}
                onSuccess={handleChannelCreated}
            />
            
            {/* S-Curve Modal */}
            <Dialog open={showSCurveModal} onOpenChange={setShowSCurveModal}>
                <DialogContent className="max-w-6xl h-[85vh] flex flex-col">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-brand-primary" />
                            Kurva S - {currentSpace?.name}
                        </DialogTitle>
                    </DialogHeader>
                    {currentWorkspace && currentSpace && (
                        <Tabs defaultValue="bulanan" className="flex-1 flex flex-col min-h-0">
                            <TabsList className="flex-shrink-0">
                                <TabsTrigger value="bulanan">Bulanan</TabsTrigger>
                                <TabsTrigger value="alltime">All Time</TabsTrigger>
                            </TabsList>
                            <TabsContent value="bulanan" className="flex-1 overflow-y-auto mt-0 pt-4">
                                <SCurveChart
                                    workspaceId={currentWorkspace.id}
                                    spaces={[]}
                                    defaultSpaceId={currentSpace.id}
                                    singleSpaceMode={true}
                                />
                            </TabsContent>
                            <TabsContent value="alltime" className="flex-1 min-h-0 mt-0 pt-2">
                                <SCurveAllTimeChart
                                    workspaceId={currentWorkspace.id}
                                    spaceId={currentSpace.id}
                                />
                            </TabsContent>
                        </Tabs>
                    )}
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

// Task Card Component for Board View
const TaskCard = ({ task, members, onClick, onStartTimer }) => {
    const priorityStyle = priorityConfig[task.priority] || priorityConfig['medium'];
    const overdue = isOverdue(task.due_date) && task.status_group !== 'Done';

    // Get assignee info
    const assignees = task.assignees || [];

    return (
        <div 
            className="kanban-task task-card"
            onClick={onClick}
            data-testid={`task-card-${task.id}`}
        >
            <div className="flex items-start justify-between mb-2">
                <span className="text-xs font-mono text-brand-primary font-semibold">{task.task_code || `#${task.task_number}`}</span>
                {task.priority !== 'none' && (
                    <span className={cn('text-xs px-2 py-0.5 rounded-full', priorityStyle.bgClass)}>
                        {priorityStyle.label}
                    </span>
                )}
            </div>
            <h4 className="font-medium text-sm text-brand-text-primary mb-2 line-clamp-2">
                {task.title}
            </h4>
            
            {/* Tags */}
            {task.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                    {task.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 bg-brand-accent-soft text-brand-primary rounded-full">
                            {tag}
                        </span>
                    ))}
                </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-brand-border">
                <div className="flex items-center gap-2">
                    {task.due_date && (
                        <span className={cn(
                            'flex items-center gap-1 text-xs',
                            overdue ? 'text-brand-error' : 'text-brand-text-muted'
                        )}>
                            <Calendar className="w-3 h-3" strokeWidth={1.5} />
                            {formatDate(task.due_date)}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {task.time_logged > 0 && (
                        <span className="text-xs text-brand-text-muted flex items-center gap-1">
                            <Clock className="w-3 h-3" strokeWidth={1.5} />
                            {formatDuration(task.time_logged)}
                        </span>
                    )}
                    <button
                        onClick={onStartTimer}
                        className="p-1 hover:bg-brand-accent-soft rounded transition-colors"
                    >
                        <Play className="w-3.5 h-3.5 text-brand-primary" strokeWidth={1.5} />
                    </button>
                    {assignees.length > 0 && (
                        <div className="avatar w-6 h-6 text-xs" title={assignees[0].name} >
                            {getInitials(assignees[0].name)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Task List Item Component for List View
const TaskListItem = ({ task, statuses, members, onClick, onStatusChange, onStartTimer, onStopTimer, isTimerActive, onQuickStatusChange }) => {
    const priorityStyle = priorityConfig[task.priority] || priorityConfig['medium'];
    const overdue = isOverdue(task.due_date) && task.status !== 'Done';
    const assignees = task.assignees || [];

    return (
        <div 
            className={cn(
                "flex items-center gap-4 p-4 cursor-pointer transition-all",
                isTimerActive 
                    ? "bg-brand-primary/10 border-l-4 border-l-brand-primary animate-pulse" 
                    : "hover:bg-brand-accent-soft/30"
            )}
            onClick={onClick}
            data-testid={`task-list-item-${task.id}`}
        >
            {/* Status Checkbox */}
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onQuickStatusChange(task.id, task.status);
                }}
                className={cn(
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0',
                    task.status === 'Done' 
                        ? 'bg-brand-success border-brand-success' 
                        : 'border-brand-border hover:border-brand-primary'
                )}
            >
                {task.status === 'Done' && (
                    <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={2} />
                )}
            </button>

            {/* Task Code */}
            <span className="text-xs font-mono text-brand-primary font-semibold flex-shrink-0">
                #{task.task_code || task.task_number}
            </span>

            {/* Title */}
            <div className="flex-1 min-w-0">
                <p className={cn(
                    'font-medium text-sm truncate',
                    task.status === 'Done' ? 'text-brand-text-muted line-through' : 'text-brand-text-primary'
                )}>
                    {task.title}
                    {isTimerActive && (
                        <span className="ml-2 text-xs text-brand-primary font-normal">(Timer Running)</span>
                    )}
                </p>
            </div>

            {/* Priority */}
            {task.priority !== 'none' && (
                <span className={cn('text-xs px-2 py-1 rounded-full flex-shrink-0', priorityStyle.bgClass)}>
                    {priorityStyle.label}
                </span>
            )}

            {/* Status */}
            <FieldStatus 
                status={task.status} 
                setStatus={(val) => {
                    onStatusChange(task.id, val);
                }}
                statuses={statuses}
                className="min-w-32 w-fit h-8" 
            >
            </FieldStatus>

            {/* Due Date */}
            {task.due_date && (
                <span className={cn(
                    'text-xs flex items-center gap-1 flex-shrink-0',
                    overdue ? 'text-brand-error' : 'text-brand-text-muted'
                )}>
                    <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {formatDate(task.due_date)}
                </span>
            )}

            {/* Assignee */}
            {assignees.length > 0 && (
                <div className="avatar w-7 h-7 text-xs flex-shrink-0" title={assignees[0].name}>
                    {getInitials(assignees[0].name)}
                </div>
            )}

            {/* Timer Button - Start or Stop */}
            {isTimerActive ? (
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 flex-shrink-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={(e) => onStopTimer(e, task)}
                >
                    <Square className="w-4 h-4" strokeWidth={1.5} />
                </Button>
            ) : (
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 flex-shrink-0"
                    onClick={(e) => onStartTimer(e, task)}
                >
                    <Play className="w-4 h-4 text-brand-primary" strokeWidth={1.5} />
                </Button>
            )}
        </div>
    );
};

// List Management Modal
const ListManagementModal = ({ 
    isOpen, 
    onClose, 
    lists, 
    editingList, 
    onCreateList, 
    onUpdateList, 
    onDeleteList,
    onEditList 
}) => {
    const [name, setName] = useState('');
    const [statuses, setStatuses] = useState([
        [
            { name: 'To Do', group: 'Not Started', color: '#9FB0AC', order: 0 },
            { name: 'In Progress', group: 'Active', color: '#6EC1E4', order: 0 },
            { name: 'Review', group: 'Review', color: '#F5C26B', order: 0 },
            { name: 'Done', group: 'Done', color: '#7ED957', order: 0 },
        ]
    ]);
    const [newStatusName, setNewStatusName] = useState('');
    const [newStatusColor, setNewStatusColor] = useState('#2F6F63');
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);

    const colorOptions = ['#2F6F63', '#1F4D45', '#7ED957', '#F5C26B', '#F26B6B', '#6EC1E4', '#9FB0AC', '#A7E3C8'];

    useEffect(() => {
        if (editingList) {
            setName(editingList.name);
            setStatuses(editingList.statuses || []);
            setShowForm(true);
        } else {
            resetForm();
        }
    }, [editingList, isOpen]);

    const resetForm = () => {
        setName('');
        setStatuses([
            { name: 'To Do', group: 'Not Started', color: '#9FB0AC', order: 0 },
            { name: 'In Progress', group: 'Active', color: '#6EC1E4', order: 0 },
            { name: 'Review', group: 'Review', color: '#F5C26B', order: 0 },
            { name: 'Done', group: 'Done', color: '#7ED957', order: 0 },
        ]);
        setShowForm(false);
    };

    const handleAddStatus = () => {
        if (newStatusName.trim()) {
            setStatuses([...statuses, { 
                name: newStatusName.trim(), 
                color: newStatusColor, 
                order: statuses.length 
            }]);
            setNewStatusName('');
            setNewStatusColor('#2F6F63');
        }
    };

    const handleRemoveStatus = (index) => {
        setStatuses(statuses.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        if (!name.trim()) {
            toast.error('List name is required');
            return;
        }
        if (statuses.length === 0) {
            toast.error('At least one status is required');
            return;
        }
        setLoading(true);
        if (editingList) {
            await onUpdateList(editingList.id, { name, statuses });
        } else {
            await onCreateList({ name, statuses });
        }
        setLoading(false);
        resetForm();
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-lg" data-testid="list-management-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ListTodo className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
                        {showForm ? (editingList ? 'Edit List' : 'Create New List') : 'Manage Lists'}
                    </DialogTitle>
                </DialogHeader>

                {!showForm ? (
                    /* Lists Overview */
                    <div className="space-y-4 mt-4">
                        <Button 
                            onClick={() => setShowForm(true)}
                            className="w-full bg-brand-primary hover:bg-brand-primary-dark"
                        >
                            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Create New List
                        </Button>

                        {lists.length > 0 ? (
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {lists.map(list => (
                                    <div 
                                        key={list.id}
                                        className="flex items-center justify-between p-3 border border-brand-border rounded-lg hover:bg-brand-accent-soft/30"
                                    >
                                        <div className="flex-1">
                                            <p className="font-medium text-sm">{list.name}</p>
                                            <div className="flex gap-1 mt-1">
                                                {list.statuses?.slice(0, 4).map((s, idx) => (
                                                    <span 
                                                        key={idx}
                                                        className="w-3 h-3 rounded-full"
                                                        style={{ backgroundColor: s.color }}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() => onEditList(list)}
                                            >
                                                <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-brand-error hover:bg-brand-error/10"
                                                onClick={() => onDeleteList(list.id)}
                                            >
                                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-center text-brand-text-muted py-4">No lists yet</p>
                        )}
                    </div>
                ) : (
                    /* Create/Edit Form */
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label>List Name</Label>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g., Sprint 1, Backlog"
                                className="mt-1"
                            />
                        </div>
                        
                        {/* Statuses */}
                        <div>
                            <Label>Workflow Statuses</Label>
                            <div className="space-y-2 mt-2 mb-3">
                                {statuses.map((status, index) => (
                                    <div key={index} className="flex items-center gap-2 p-2 bg-brand-accent-soft/30 rounded-lg">
                                        <div 
                                            className="w-4 h-4 rounded-full flex-shrink-0"
                                            style={{ backgroundColor: status.color }}
                                        />
                                        <span className="flex-1 text-sm">{status.name}</span>
                                        <button
                                            onClick={() => handleRemoveStatus(index)}
                                            className="text-brand-text-muted hover:text-brand-error transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            
                            <div className="flex gap-2 items-end">
                                <div className="flex-1">
                                    <Input
                                        value={newStatusName}
                                        onChange={(e) => setNewStatusName(e.target.value)}
                                        placeholder="New status"
                                        onKeyDown={(e) => e.key === 'Enter' && handleAddStatus()}
                                    />
                                </div>
                                <div className="flex gap-1">
                                    {colorOptions.slice(0, 4).map(c => (
                                        <button
                                            key={c}
                                            onClick={() => setNewStatusColor(c)}
                                            className={cn(
                                                'w-6 h-6 rounded-full transition-all',
                                                newStatusColor === c && 'ring-2 ring-offset-1 ring-brand-primary'
                                            )}
                                            style={{ backgroundColor: c }}
                                        />
                                    ))}
                                </div>
                                <Button variant="outline" size="sm" onClick={handleAddStatus}>
                                    <Plus className="w-4 h-4" strokeWidth={1.5} />
                                </Button>
                            </div>
                        </div>

                        <div className="flex justify-between pt-4">
                            <Button variant="outline" onClick={() => { resetForm(); onEditList(null); }}>
                                Back
                            </Button>
                            <Button 
                                onClick={handleSubmit}
                                className="bg-brand-primary hover:bg-brand-primary-dark"
                                disabled={loading}
                            >
                                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                {editingList ? 'Save Changes' : 'Create List'}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

// Timeline View Component with Planning and Realization bars
const TimelineView = ({ tasks, statuses, onTaskClick, onStartTimer, onStatusChange }) => {
    const { currentSpace } = useWorkspace();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [showPlanning, setShowPlanning] = useState(true);
    const [showRealization, setShowRealization] = useState(true);
    const [timelineTasks, setTimelineTasks] = useState([]);
    const [loadingTimeline, setLoadingTimeline] = useState(false);
    
    // Calculate timeline range (show full month)
    const startDate = startOfMonth(currentDate);
    const endDate = endOfMonth(currentDate);
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    
    // Fetch timeline data when space or date changes
    useEffect(() => {
        const fetchTimeline = async () => {
            if (!currentSpace?.id) return;
            
            setLoadingTimeline(true);
            try {
                const startDateStr = format(startDate, 'yyyy-MM-dd');
                const endDateStr = format(endDate, 'yyyy-MM-dd');
                const { data } = await timelineAPI.getSpaceTimeline(currentSpace.id, startDateStr, endDateStr);
                setTimelineTasks(data);
            } catch (error) {
                console.error('Failed to fetch timeline:', error);
                // Fallback to regular tasks
                setTimelineTasks(tasks);
            } finally {
                setLoadingTimeline(false);
            }
        };
        
        fetchTimeline();
    }, [currentSpace?.id, currentDate, tasks]);
    
    // Get status color
    const getStatusColor = (statusName) => {
        const status = statuses.find(s => s.name.toLowerCase() === statusName?.toLowerCase());
        return status?.color || '#9FB0AC';
    };
    
    // Sort tasks by due_date - use timeline data with realization info
    const sortedTasks = [...timelineTasks].sort((a, b) => {
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
    });

    const calculatePlanningBar = (task) => {
        // Use planned_end from timeline API, fallback to due_date
        const plannedEnd = task.planned_end || task.due_date;
        if (!plannedEnd) return null;
        
        const dueDate = parseISO(plannedEnd);
        const estimateDays = task.time_estimate ? Math.ceil(task.time_estimate / (8 * 60)) : 1;
        
        // Use planned_start if available, otherwise calculate from estimate
        let startTaskDate;
        if (task.planned_start) {
            startTaskDate = parseISO(task.planned_start);
        } else {
            startTaskDate = subDays(dueDate, estimateDays);
        }
        
        // Calculate position
        const startOffset = Math.max(0, differenceInDays(startTaskDate, startDate));
        const endOffset = Math.min(days.length, differenceInDays(dueDate, startDate) + 1);
        const width = Math.max(1, endOffset - startOffset);
        
        // Don't render if completely outside view
        if (endOffset <= 0 || startOffset >= days.length) return null;
        
        return {
            left: `${(startOffset / days.length) * 100}%`,
            width: `${(width / days.length) * 100}%`,
        };
    };

    const calculateRealizationBar = (task) => {
        // Realisasi bar shown when:
        // 1. Task has actual_start and actual_end from backend
        // 2. Task is Done, In Progress, Review, or has active timer
        
        const isDone = task.status?.toLowerCase() === 'done';
        const isInProgress = task.status?.toLowerCase() === 'in progress';
        const isReview = task.status?.toLowerCase() === 'review';
        
        // Check if we have realization data from backend
        const realization_start = task.actual_start;
        const realization_end = task.actual_end;
        
        // If no start date from backend, no bar to show
        if (!realization_start) return null;
        
        // If no end date, only show if task is in progress/review/has timer
        const today = format(new Date(), 'yyyy-MM-dd');
        let endDate = realization_end;
        
        if (!endDate) {
            if (task.has_active_timer || isInProgress || isReview) {
                endDate = today;
            } else if (isDone) {
                // Done but no done_date - use today as fallback
                endDate = today;
            } else {
                return null;
            }
        }
        
        const realStartDate = parseISO(realization_start);
        const realEndDate = parseISO(endDate);
        
        const startOffset = Math.max(0, differenceInDays(realStartDate, startDate));
        const endOffset = Math.min(days.length, differenceInDays(realEndDate, startDate) + 1);
        // Minimum 1 day width (pembulatan ke hari)
        const width = Math.max(1, endOffset - startOffset);
        
        return {
            left: `${(startOffset / days.length) * 100}%`,
            width: `${(width / days.length) * 100}%`,
        };
    };

    const getRealizationColor = (task) => {
        // Yellow if timer is running or In Progress
        if (task.has_active_timer) return '#F59E0B'; // Amber/Yellow - Running
        // Blue if In Progress
        if (task.status?.toLowerCase() === 'in progress') return '#3B82F6'; // Blue - In Progress
        // Orange if Review
        if (task.status?.toLowerCase() === 'review') return '#F97316'; // Orange - Review
        // Green if task is Done (completed)
        if (task.status?.toLowerCase() === 'done') return '#22C55E'; // Green - Done
        // Red if exceeds planning
        if (task.exceeds_plan) return '#EF4444'; // Red
        // Default blue for in progress
        return '#3B82F6';
    };

    const prevMonth = () => setCurrentDate(subDays(startDate, 1));
    const nextMonth = () => setCurrentDate(addDays(endDate, 1));

    return (
        <Card className="border-brand-border overflow-hidden" data-testid="timeline-view">
            {/* Timeline Header */}
            <div className="flex items-center justify-between p-4 border-b border-brand-border bg-brand-accent-soft/30">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={prevMonth}>
                            <ChevronDown className="w-4 h-4 rotate-90" />
                        </Button>
                        <span className="font-medium text-brand-text-primary">
                            {format(currentDate, 'MMMM yyyy')}
                        </span>
                        <Button variant="ghost" size="icon" onClick={nextMonth}>
                            <ChevronDown className="w-4 h-4 -rotate-90" />
                        </Button>
                    </div>
                </div>
                
                {/* Filter toggles */}
                <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                            type="checkbox" 
                            checked={showPlanning} 
                            onChange={(e) => setShowPlanning(e.target.checked)}
                            className="rounded border-brand-border text-brand-primary focus:ring-brand-primary"
                        />
                        <span className="text-sm flex items-center gap-1.5">
                            <span className="w-3 h-3 rounded bg-gray-400" />
                            Planning
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                            type="checkbox" 
                            checked={showRealization} 
                            onChange={(e) => setShowRealization(e.target.checked)}
                            className="rounded border-brand-border text-brand-primary focus:ring-brand-primary"
                        />
                        <span className="text-sm flex items-center gap-1.5">
                            <span className="w-3 h-3 rounded bg-green-500" />
                            Realisasi
                        </span>
                    </label>
                    <div className="text-sm text-brand-text-muted">
                        {loadingTimeline ? 'Loading...' : `${sortedTasks.length} tasks`}
                    </div>
                </div>
            </div>
            
            {/* Legend */}
            <div className="flex items-center gap-4 px-4 py-2 border-b border-brand-border bg-white text-xs flex-wrap">
                <span className="text-brand-text-muted">Legend:</span>
                <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-gray-400" />
                    <span>Planning</span>
                </div>
                <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-yellow-500" />
                    <span>Timer Berjalan</span>
                </div>
                <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-blue-500" />
                    <span>In Progress</span>
                </div>
                <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-orange-500" />
                    <span>Review</span>
                </div>
                <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-green-500" />
                    <span>Done</span>
                </div>
            </div>

            {/* Timeline Grid */}
            <div className="overflow-x-auto">
                <div className="min-w-[800px]">
                    {/* Days Header */}
                    <div className="flex border-b border-brand-border bg-white sticky top-0 z-10">
                        <div className="w-64 flex-shrink-0 p-3 border-r border-brand-border bg-brand-background">
                            <span className="text-xs font-semibold text-brand-text-muted uppercase">Task</span>
                        </div>
                        <div className="flex-1 flex">
                            {days.map((day, idx) => (
                                <div 
                                    key={idx} 
                                    className={cn(
                                        "flex-1 text-center py-2 border-r border-brand-border text-xs",
                                        format(day, 'E') === 'Sat' || format(day, 'E') === 'Sun' 
                                            ? 'bg-brand-accent-soft/50' : 'bg-white'
                                    )}
                                >
                                    <div className="text-brand-text-muted">{format(day, 'E')}</div>
                                    <div className="font-medium text-brand-text-primary">{format(day, 'd')}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Tasks Rows */}
                    {sortedTasks.length > 0 ? (
                        sortedTasks.map((task) => {
                            const planningBar = calculatePlanningBar(task);
                            const realizationBar = calculateRealizationBar(task);
                            const priorityStyle = priorityConfig[task.priority] || priorityConfig['medium'];
                            const overdue = isOverdue(task.due_date) && task.status !== 'Done';
                            
                            return (
                                <div 
                                    key={task.id} 
                                    className="flex border-b border-brand-border hover:bg-brand-accent-soft/20 cursor-pointer"
                                    onClick={() => onTaskClick(task)}
                                    data-testid={`timeline-task-${task.id}`}
                                >
                                    {/* Task Info */}
                                    <div className="w-64 flex-shrink-0 p-3 border-r border-brand-border">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <span className="text-xs font-mono text-brand-primary font-semibold">
                                                    {task.task_code || `#${task.task_number}`}
                                                </span>
                                                <p className="text-sm font-medium text-brand-text-primary truncate mt-0.5">
                                                    {task.title}
                                                </p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    {task.due_date && (
                                                        <span className={cn(
                                                            "text-xs flex items-center gap-1",
                                                            overdue ? 'text-brand-error' : 'text-brand-text-muted'
                                                        )}>
                                                            <Calendar className="w-3 h-3" />
                                                            {formatDate(task.due_date)}
                                                        </span>
                                                    )}
                                                    {task.time_estimate && (
                                                        <span className="text-xs text-brand-text-muted flex items-center gap-1">
                                                            <Clock className="w-3 h-3" />
                                                            {formatDuration(task.time_estimate)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {task.priority !== 'none' && (
                                                <span className={cn('text-xs px-1.5 py-0.5 rounded-full flex-shrink-0', priorityStyle.bgClass)}>
                                                    {priorityStyle.label?.charAt(0)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Timeline Bars (Planning + Realization) */}
                                    <div className="flex-1 relative py-2">
                                        {/* Background grid */}
                                        <div className="absolute inset-0 flex">
                                            {days.map((_, idx) => (
                                                <div 
                                                    key={idx} 
                                                    className={cn(
                                                        "flex-1 border-r border-brand-border",
                                                        idx % 7 >= 5 && 'bg-brand-accent-soft/30'
                                                    )}
                                                />
                                            ))}
                                        </div>
                                        
                                        {/* Planning Bar (top) */}
                                        {showPlanning && planningBar && (
                                            <div 
                                                className="absolute h-4 rounded flex items-center px-2"
                                                style={{
                                                    left: planningBar.left,
                                                    width: planningBar.width,
                                                    top: '4px',
                                                    backgroundColor: '#9CA3AF', // Gray
                                                    minWidth: '30px',
                                                }}
                                            >
                                                <span className="text-[10px] text-white font-medium truncate">
                                                    Plan
                                                </span>
                                            </div>
                                        )}
                                        
                                        {/* Realization Bar (bottom) */}
                                        {showRealization && realizationBar && (
                                            <div 
                                                className="absolute h-4 rounded flex items-center px-2"
                                                style={{
                                                    left: realizationBar.left,
                                                    width: realizationBar.width,
                                                    bottom: '4px',
                                                    backgroundColor: getRealizationColor(task),
                                                    minWidth: '30px',
                                                }}
                                            >
                                                <span className="text-[10px] text-white font-medium truncate">
                                                    {task.has_active_timer ? '⏱ Running' : 
                                                     task.status?.toLowerCase() === 'done' ? 'Selesai' : 
                                                     task.status?.toLowerCase() === 'in progress' ? 'Progres' :
                                                     task.status?.toLowerCase() === 'review' ? 'Review' : 'Real'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="p-8 text-center text-brand-text-muted">
                            No tasks with due dates. Create your first task!
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};
